import { schemas } from '#oaf_schema.registry';
import { createStore } from '@oneappexchange/lwc-wired';
import { IS_DEVELOPMENT } from '~/config';
import { handleException, throwDevelopmentOrLog } from '~/modules/utilities/errorUtilities';
import { ListingEditorStatus } from '~/modules/utilities/listingEditorStatus';
import { getDefaultProvisionFlowByExtensionType } from '~/modules/utilities/listingUtilities';
import { notifyFailure } from '~/modules/utilities/notificationUtilities';
import { isConsultantType, isCopilotType, isLinkType, isTableauType, SOLUTION_IDS, TECHNOLOGY_EXTENSION_MAP, TECHNOLOGY_SOLUTION_EXTENSION_MAP, TECHNOLOGY_SOLUTION_MAP, TECHNOLOGY_TEST_DRIVE_MAP } from '~/modules/utilities/schemaUtilities';
import { i18nStore } from '../i18nStore';
import { listingStoreValidator } from './listingStore';

// Creating our own type for force listing since, on the MERCURY (UI) this type does not match the OAF type.
// In mercury some properties can be removed by the users (oaf wouldn't allow that) so in the "MERCURY"
// types, we can define which properties we can expect to be empty. This makes TS happy.

const initialState = {
  baseGet: () => ({
    listing: null
  }),
  type: 'force/package',
  data: schemas['listing/extensions/force/listings/Listing'].new()
};
export const forceListingStore = createStore(initialState, (set, get) => ({
  reset() {
    set(function reset() {
      return initialState;
    });
  },
  initialize(baseGet, editorStatus, extensionData, {
    solution,
    trialTemplate,
    marketplace
  }) {
    set(function initialize(draft) {
      var _baseGet$listing$tech, _baseGet$listing;
      draft.baseGet = baseGet;
      draft.type = (_baseGet$listing$tech = (_baseGet$listing = baseGet().listing) === null || _baseGet$listing === void 0 ? void 0 : _baseGet$listing.technology) !== null && _baseGet$listing$tech !== void 0 ? _baseGet$listing$tech : initialState.type;
      draft.solution = solution;
      draft.trialTemplate = trialTemplate;
      draft.data = schemas[TECHNOLOGY_EXTENSION_MAP[draft.type]].new(extensionData);
      draft.selectedSolutionId = undefined;
      draft.selectedPackageVersionId = undefined;
      draft.selectedPackageisManaged = undefined;
      draft.selectedPackagelmaOrganizationId = undefined;

      // Initialize selected trial template ID
      if (trialTemplate) {
        const {
          trialTemplateId
        } = trialTemplate.solution;
        if (trialTemplate.technology !== 'TrialTemplate') {
          throwDevelopmentOrLog(new Error(`Found trialTemplate but its technology type (${trialTemplate.technology}) is not supported`));
        } else if (!trialTemplateId) {
          throwDevelopmentOrLog(new Error('Found trialTemplate but it was missing trialTemplateId'));
        } else {
          draft.selectedTrialTemplateId = trialTemplateId;
        }
      }

      // Initialize selected package ID
      if (solution) {
        if (!SOLUTION_IDS.includes(solution.technology)) {
          throwDevelopmentOrLog(new Error(`Found solution but its technology type (${solution.technology}) is not supported'`));
        } else if (solution.technology === 'force/Package') {
          const {
            packageId,
            version
          } = solution.solution;
          if (version.packageVersionId) {
            draft.selectedSolutionId = packageId;
            draft.selectedPackageVersionId = version.packageVersionId;
            draft.selectedPackageisManaged = version.isManaged;
            draft.selectedPackagelmaOrganizationId = version.lmaOrganizationId;
          } else {
            throwDevelopmentOrLog(new Error('Found package solution but it was missing packageVersionId'));
          }
        } else {
          draft.selectedSolutionId = solution.id;
        }
      }

      // Initialize highlights
      if (draft.data.highlights.length === 0) {
        draft.data.highlights.push('');
      }

      // Initialize marketplace (it can only be set once on new listings, cannot be changed on existing listings)
      if (marketplace && editorStatus === ListingEditorStatus.NewListing) {
        draft.data.marketplace = marketplace;
      }
      // Set default values for new Copilot listings
      if (editorStatus === ListingEditorStatus.NewListing && draft.type === 'force/copilot') {
        draft.data.languages = ['en'];
        draft.data.productsRequired = ['Einstein 1'];
        draft.data.isEinsteinAgent = true;
      }
    });
  },
  upsertExtension(extension, draft) {
    if (draft) {
      const index = draft.data.extensions.findIndex(({
        extensionType
      }) => extensionType === extension.extensionType);
      if (index === -1) {
        draft.data.extensions.push(extension);
      } else {
        draft.data.extensions[index].data = extension.data;
      }
    }
    return extension;
  },
  upsertTestDriveExtension(extension, draft) {
    if (draft) {
      const index = draft.data.extensions.findIndex(({
        extensionType
      }) => extensionType === extension.extensionType);
      if (index === -1) {
        draft.data.extensions.push(extension);
      } else {
        draft.data.extensions[index].data = extension.data;
      }
    }
    return extension;
  },
  /* istanbul ignore next */
  setLanguages(languages) {
    set(function setLanguages(draft) {
      draft.data.languages = languages;
    });
  },
  /* istanbul ignore next */
  setSalesforceEditions(editions) {
    set(function setSalesforceEditions(draft) {
      draft.data.editions = editions;
    });
  },
  /* istanbul ignore next */
  setRequiredProducts(products) {
    set(function setRequiredProducts(draft) {
      draft.data.productsRequired = products;
    });
  },
  setRequiredListings(listings) {
    set(function setRequiredListings(draft) {
      draft.data.listingsRequired = listings;
    });
  },
  /* istanbul ignore next */
  setSupportedProducts(products) {
    set(function setSupportedProducts(draft) {
      draft.data.productsSupported = products;
    });
  },
  /* istanbul ignore next */
  setSupportedIndustries(supportedIndustries) {
    set(function setSupportedIndustries(draft) {
      draft.data.supportedIndustries = supportedIndustries;
    });
  },
  /* istanbul ignore next */
  setTargetUserPersona(targetUserPersona) {
    set(function setTargetUserPersona(draft) {
      draft.data.targetUserPersona = targetUserPersona;
    });
  },
  setInstall(installOption) {
    set(function setInstall(draft) {
      const packageListing = selectSolutionExtensionData(draft);
      packageListing.provisionFlow = installOption;
      selectSolutionExtensionData(draft, true).installUrl = undefined;
    });
  },
  setCopilotDescription(copilotDescription) {
    set(function setCopilotDescription(draft) {
      if (!isCopilotType(draft.type)) return;
      draft.data.description = copilotDescription;
    });
  },
  setNumAgents(numAgents) {
    set(function setNumAgents(draft) {
      if (!isCopilotType(draft.type)) return;
      draft.data.numAgents = numAgents;
    });
  },
  setNumTopics(numTopics) {
    set(function setNumTopics(draft) {
      if (!isCopilotType(draft.type)) return;
      draft.data.numTopics = numTopics;
    });
  },
  setNumActions(numActions) {
    set(function setNumActions(draft) {
      if (!isCopilotType(draft.type)) return;
      draft.data.numActions = numActions;
    });
  },
  setHasExternalLLM(hasExternalLLM) {
    set(function setHasExternalLLM(draft) {
      if (!isCopilotType(draft.type)) return;
      draft.data.containsExternalLLM = hasExternalLLM;
    });
  },
  /* istanbul ignore next */
  setInstallUrl(url = '') {
    set(function setInstallUrl(draft) {
      selectSolutionExtensionData(draft, true).installUrl = url.trim() === '' ? undefined : url;
    });
  },
  /* istanbul ignore next */
  setIsPledge1Percent(isPledge1Percent) {
    set(function setIsPledge1Percent(draft) {
      draft.data.isPledge1Percent = isPledge1Percent;
    });
  },
  /* istanbul ignore next */
  setIsDiverseOwnedBusiness(isDiverseOwnedBusiness) {
    set(function setIsDiverseOwnedBusiness(draft) {
      draft.data.isDiverseOwnedBusiness = isDiverseOwnedBusiness;
    });
  },
  /* istanbul ignore next */
  setIsAccessibleSolution(isAccessibleSolution) {
    set(function setIsAccessibleSolution(draft) {
      draft.data.isAccessibleSolution = isAccessibleSolution;
    });
  },
  /* istanbul ignore next */
  setDiverseOwnedBusinessURL(diverseOwnedBusinessURL) {
    set(function setDiverseOwnedBusinessURL(draft) {
      draft.data.diverseOwnedBusinessURL = diverseOwnedBusinessURL;
    });
  },
  /* istanbul ignore next */
  setAccessibleSolutionURL(accessibleSolutionURL) {
    set(function setAccessibleSolutionURL(draft) {
      draft.data.accessibleSolutionURL = accessibleSolutionURL;
    });
  },
  setSupportedFeatures(supportedFeatures) {
    set(function setSupportedFeatures(draft) {
      draft.data.isSalesforceShield = supportedFeatures.includes('isSalesforceShield');
      draft.data.isMultiCurrency = supportedFeatures.includes('isMultiCurrency');
      draft.data.isPersonAccounts = supportedFeatures.includes('isPersonAccounts');
      draft.data.isLightningReady = supportedFeatures.includes('isLightningReady');
      draft.data.isSalesforce1Mobile = supportedFeatures.includes('isSalesforce1Mobile');
      draft.data.isLightningComponent = supportedFeatures.includes('isLightningComponent');
      draft.data.isSalesProgram = supportedFeatures.includes('isSalesProgram');
      if (draft.type === 'force/package' || draft.type === 'force/copilot') {
        draft.data.isEinsteinAgent = supportedFeatures.includes('isEinsteinAgent');
      }
      // TODO: Include the other fields
      // FedRAMP Certified
      // HIPAA Compliant
      // Minority Owned
    });
  },
  /* istanbul ignore next */
  setTermsAndConditions(termsAndConditions) {
    set(function setTermsAndConditions(draft) {
      draft.data.licenseAgreement = termsAndConditions;
    });
  },
  /* istanbul ignore next */
  setTagLine(tagLine) {
    set(function setTagLine(draft) {
      draft.data.searchBlurb = tagLine;
    });
  },
  /* istanbul ignore next */
  setCategories(listingCategories) {
    set(function setCategories(draft) {
      draft.data.listingCategories = listingCategories;
    });
  },
  addHighlight() {
    set(function addHighlight(draft) {
      if (draft.data.highlights) {
        draft.data.highlights.push('');
      } else {
        draft.data.highlights = [''];
      }
    });
  },
  changeHighlight(index, value) {
    set(function changeHighlight(draft) {
      if (draft.data.highlights) {
        draft.data.highlights[index] = value;
      }
    });
  },
  removeHighlight(index) {
    set(function removeHighlight(draft) {
      if (draft.data.highlights) {
        draft.data.highlights.splice(index, 1);
      }
    });
  },
  /* istanbul ignore next */
  setAdditionalRequirements(additionalRequirements) {
    set(function setAdditionalRequirements(draft) {
      draft.data.systemRequirements = additionalRequirements;
    });
  },
  setPackage(packageInfo, packageVersion) {
    set(function setPackageSelection(draft) {
      draft.selectedSolutionId = packageInfo.id;
      draft.selectedPackageVersionId = packageVersion.packageVersionId;
      draft.selectedPackageisManaged = packageVersion.isManaged;
      draft.selectedPackagelmaOrganizationId = packageVersion.lmaOrganizationId;
    });
    set(function setPackageExtension(draft) {
      // solutionId gets set when saving a listing (we exchange selectedSolutionId for it)
      selectSolutionExtensionData(draft, true).solutionId = '';
    });
  },
  setSolutionId(solutionId) {
    set(function setSolutionSelection(draft) {
      draft.selectedSolutionId = solutionId;
    });
    set(function setSolutionExtension(draft) {
      selectSolutionExtensionData(draft, true).solutionId = solutionId;
    });
  },
  removeSolution() {
    set(function removeSolution(draft) {
      var _draft$data$extension;
      draft.selectedSolutionId = undefined;
      const matchType = TECHNOLOGY_SOLUTION_EXTENSION_MAP[get().type];
      const index = (_draft$data$extension = draft.data.extensions.findIndex(({
        extensionType
      }) => extensionType === matchType)) !== null && _draft$data$extension !== void 0 ? _draft$data$extension : -1;
      if (index !== -1) {
        draft.data.extensions.splice(index, 1);
      }
    });
  },
  /* istanbul ignore next */
  setSelectedTrialTemplateId(id) {
    set(function setSelectedTrialTemplateId(draft) {
      draft.selectedTrialTemplateId = id;
    });
  },
  setSelfAttestation(selfAttestation) {
    set(function setSelfAttestation(draft) {
      draft.data.selfAttestation = selfAttestation;
      draft.data.selfAttestationDate = '';
      if (selfAttestation) {
        draft.data.selfAttestationDate = new Date().toISOString();
      }
    });
  },
  /* istanbul ignore next */
  setTestDriveOrgId(orgId) {
    set(function setTestDriveOrgId(draft) {
      const testDrive = selectTestDrive(draft, true);
      if (testDrive) {
        testDrive.testDriveOrgID = orgId;
      }
    });
  },
  /* istanbul ignore next */
  setTestDriveUsername(username) {
    set(function setTestDriveUsername(draft) {
      const testDrive = selectTestDrive(draft, true);
      if (testDrive) {
        testDrive.testDriveUserName = username;
      }
    });
  },
  /* istanbul ignore next */
  setTestDrivePassword(password) {
    set(function setTestDrivePassword(draft) {
      const testDrive = selectTestDrive(draft, true);
      if (testDrive) {
        testDrive.testDrivePassword = password;
      }
    });
  },
  /* istanbul ignore next */
  setEnableTestDrive(value) {
    set(function setEnableTestDrive(draft) {
      const testDrive = selectTestDrive(draft, true);
      if (testDrive) {
        testDrive.enableTestDrive = value;
      }
    });
  },
  /* istanbul ignore next */
  setCreditorIdentifier(creditorIdentifier) {
    set(function setCreditorIdentifier(draft) {
      draft.data.creditorIdentifier = creditorIdentifier;
    });
  },
  deleteTestDriveData() {
    set(function deleteTestDriveData(draft) {
      const testDrive = selectTestDrive(draft, true);
      if (testDrive) {
        if (testDrive.enableTestDrive) {
          return;
        }
        testDrive.testDriveOrgID = '';
        testDrive.testDrivePassword = '';
        testDrive.testDriveUserName = '';
      }
    });
  }
}), 'FORCE_LISTING', IS_DEVELOPMENT);
function updateListingStoreValidator() {
  listingStoreValidator.resetCachedStepsCompleted();
}
forceListingStore.subscribe(updateListingStoreValidator, selectPackageVersionId);
export function selectForceListingData(state) {
  return state.data;
}
export function selectSolutionExtension(state, isDraftState) {
  const extensionType = TECHNOLOGY_SOLUTION_EXTENSION_MAP[state.type];
  let solutionExtension = state.data.extensions.find(test => test.extensionType === extensionType);
  let provisionFlow;
  if (!solutionExtension || !solutionExtension.data.provisionFlow) {
    try {
      provisionFlow = getDefaultProvisionFlowByExtensionType(state.type);
    } catch (err) {
      handleException(err, `Invalid Extension Type`);
      notifyFailure(i18nStore.getState().i18nStaticCache.notifications.genericError);
      provisionFlow = {};
    }
    solutionExtension = forceListingStore.upsertExtension({
      extensionType,
      data: schemas[extensionType].new(provisionFlow)
    }, isDraftState ? state : undefined);
  }
  return solutionExtension;
}
export function selectSolutionExtensionData(state, isDraftState) {
  const solutionExtension = selectSolutionExtension(state, isDraftState);
  return solutionExtension.data;
}
export function selectSolutionId(state) {
  return state.selectedSolutionId;
}
export function selectPackageVersionId(state) {
  return state.selectedPackageVersionId;
}
export function selectPackageisManaged(state) {
  return state.selectedPackageisManaged;
}
export function selectPackagelmaOrganizationId(state) {
  return state.selectedPackagelmaOrganizationId;
}
export function selectTrialTemplateId(state) {
  return state.selectedTrialTemplateId;
}
export function selectProvisionFlow(state) {
  const extensionType = TECHNOLOGY_SOLUTION_EXTENSION_MAP[state.type];
  const solutionExtension = state.data.extensions.find(test => test.extensionType === extensionType);
  if (solutionExtension) {
    return selectSolutionExtensionData(state).provisionFlow || '';
  }
  return '';
}
export function selectInstallUrl(state) {
  return selectSolutionExtensionData(state).installUrl || '';
}
export function selectTestDrive(state, isDraftState) {
  var _solutionExtensionDat;
  if (isLinkType(TECHNOLOGY_SOLUTION_MAP[state.type]) || isConsultantType(state.type) || isTableauType(state.type)) {
    return null;
  }
  const extensionType = TECHNOLOGY_TEST_DRIVE_MAP[state.type];
  const solutionExtension = selectSolutionExtension(state, isDraftState);
  const solutionExtensionData = solutionExtension.data;
  let testDriveExtension = (_solutionExtensionDat = solutionExtensionData.extensions) === null || _solutionExtensionDat === void 0 ? void 0 : _solutionExtensionDat.find(test => test.extensionType === extensionType);
  if (!testDriveExtension) {
    solutionExtensionData.extensions = solutionExtensionData.extensions ? solutionExtensionData.extensions : [];
    testDriveExtension = forceListingStore.upsertTestDriveExtension({
      extensionType,
      data: schemas[extensionType].new({
        enableTestDrive: true,
        testDriveOrgID: '',
        testDrivePassword: '',
        testDriveUserName: ''
      })
    }, isDraftState ? solutionExtension : undefined);
  }
  return testDriveExtension.data;
}
export function selectSupportFeatures({
  data
}) {
  const supportedFeatures = [];
  if (data.isLightningReady) {
    supportedFeatures.push('isLightningReady');
  }
  if (data.isMultiCurrency) {
    supportedFeatures.push('isMultiCurrency');
  }
  if (data.isPersonAccounts) {
    supportedFeatures.push('isPersonAccounts');
  }
  if (data.isSalesforceShield) {
    supportedFeatures.push('isSalesforceShield');
  }
  if (data.isSalesforce1Mobile) {
    supportedFeatures.push('isSalesforce1Mobile');
  }
  if (data.isLightningComponent) {
    supportedFeatures.push('isLightningComponent');
  }
  if (data.isSalesProgram) {
    supportedFeatures.push('isSalesProgram');
  }
  if (data.isEinsteinAgent) {
    supportedFeatures.push('isEinsteinAgent');
  }
  return supportedFeatures;
}
export function selectHighlights(state) {
  return state.data.highlights;
}
export function selectSelfAttestation(state) {
  return state.data.selfAttestation || false;
}
export function selectSelfAttestationDate(state) {
  return state.data.selfAttestationDate || '';
}
export function selectCreditorIdentifier(state) {
  return state.data.creditorIdentifier || '';
}
function selectCopilotProperty(state, selector) {
  return isCopilotType(state.type) ? selector(state.data) : undefined;
}
export function selectCopilotDescription(state) {
  return selectCopilotProperty(state, data => data.description);
}
export function selectNumAgents(state) {
  return selectCopilotProperty(state, data => data.numAgents);
}
export function selectNumTopics(state) {
  return selectCopilotProperty(state, data => data.numTopics);
}
export function selectNumActions(state) {
  return selectCopilotProperty(state, data => data.numActions);
}
export function selectExternalLLM(state) {
  return selectCopilotProperty(state, data => data === null || data === void 0 ? void 0 : data.containsExternalLLM) || false;
}